import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface DocumentTextPage {
  id: string;
  content_id: string;
  page_number: number;
  text: string;
  word_count: number | null;
  created_at: string;
}

export function useDocumentText(contentId: string | undefined) {
  return useQuery({
    queryKey: ['document-text', contentId],
    queryFn: async () => {
      if (!contentId) return [];
      
      const { data, error } = await supabase
        .from('document_text')
        .select('*')
        .eq('content_id', contentId)
        .order('page_number', { ascending: true });
      
      if (error) throw error;
      return data as DocumentTextPage[];
    },
    enabled: !!contentId,
  });
}

export function useDocumentTextPage(contentId: string | undefined, pageNumber: number) {
  return useQuery({
    queryKey: ['document-text-page', contentId, pageNumber],
    queryFn: async () => {
      if (!contentId || !pageNumber) return null;
      
      const { data, error } = await supabase
        .from('document_text')
        .select('*')
        .eq('content_id', contentId)
        .eq('page_number', pageNumber)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error; // Ignore not found
      return data as DocumentTextPage | null;
    },
    enabled: !!contentId && pageNumber > 0,
  });
}

export function useTextExtractionStatus(contentId: string | undefined) {
  const queryClient = useQueryClient();
  const [realtimeStatus, setRealtimeStatus] = useState<string | null>(null);

  // Initial fetch
  const query = useQuery({
    queryKey: ['text-extraction-status', contentId],
    queryFn: async () => {
      if (!contentId) return null;
      
      const { data, error } = await supabase
        .from('content')
        .select('text_extraction_status')
        .eq('id', contentId)
        .single();
      
      if (error) throw error;
      return data?.text_extraction_status as string | null;
    },
    enabled: !!contentId,
  });

  // Realtime subscription for status updates
  useEffect(() => {
    if (!contentId) return;

    const channel = supabase
      .channel(`text-extraction-status-${contentId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'content',
          filter: `id=eq.${contentId}`,
        },
        (payload) => {
          const newStatus = (payload.new as any).text_extraction_status;
          console.log('[useTextExtractionStatus] Realtime update:', newStatus);
          setRealtimeStatus(newStatus);
          
          // If status changed to 'ready', refetch document text
          if (newStatus === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['document-text', contentId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contentId, queryClient]);

  return {
    ...query,
    data: realtimeStatus ?? query.data,
  };
}

export function useExtractDocumentText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contentId, forceRefresh = false }: { contentId: string; forceRefresh?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('extract-document-text', {
        body: { contentId, forceRefresh },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { contentId }) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['document-text', contentId] });
      queryClient.invalidateQueries({ queryKey: ['text-extraction-status', contentId] });
    },
  });
}

// Helper to save document text from frontend PDF.js extraction
export function useSaveDocumentText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      contentId, 
      pages 
    }: { 
      contentId: string; 
      pages: Array<{ pageNumber: number; text: string; wordCount?: number }>;
    }) => {
      // Delete existing text for this content
      await supabase
        .from('document_text')
        .delete()
        .eq('content_id', contentId);

      // Insert new pages
      const rows = pages.map(p => ({
        content_id: contentId,
        page_number: p.pageNumber,
        text: p.text,
        word_count: p.wordCount || p.text.split(/\s+/).filter(Boolean).length,
      }));

      const { error } = await supabase
        .from('document_text')
        .insert(rows);

      if (error) throw error;

      // Update extraction status
      await supabase
        .from('content')
        .update({ text_extraction_status: 'ready' })
        .eq('id', contentId);

      return { success: true, pageCount: pages.length };
    },
    onSuccess: (_, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: ['document-text', contentId] });
      queryClient.invalidateQueries({ queryKey: ['text-extraction-status', contentId] });
    },
  });
}